// JoinMeetingLayout.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
// import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Users,
  RefreshCw,
  LogIn,
} from "lucide-react";
import { toast } from "sonner";

import {
  getTableParticipants,
  leaveSessionTable,
  leaveSessionTableKeepalive,
  joinSessionTable,
} from "@/Services/EventMeetingServices";

const film = "https://placehold.net/200x200.png?text=Table+Icon";
// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const SEAT_LABELS_8 = ["A", "B", "C", "D", "E", "F", "G", "H"];

const DEVICE_ERRORS = {
  MIC_DENIED: {
    message:
      "Microphone access denied. Click the [i] icon in your browser's address bar to allow access.",
  },
  CAMERA_DENIED: {
    message:
      "Camera access denied. Click the [i] icon in your browser's address bar to allow access.",
  },
  MIC_IN_USE: {
    message:
      "Microphone is being used by another application. Please close other apps and try again.",
  },
  CAMERA_IN_USE: {
    message:
      "Camera is being used by another application. Please close other apps and try again.",
  },
  MIC_NOT_FOUND: {
    message: "No microphone found. Please connect a microphone and try again.",
  },
  CAMERA_NOT_FOUND: {
    message: "No camera found. Please connect a camera and try again.",
  },
  GENERIC_ERROR: {
    message:
      "Could not access camera/microphone. Please check browser permissions.",
  },
};

// ---------------------------------------------------------------------------
// PURE UTILITIES
// ---------------------------------------------------------------------------

const joinCacheKey = (sessionId, tableId) =>
  `livekitJoin:${String(sessionId)}:${String(tableId)}`;

const getName = (u) =>
  u?.name ??
  u?.fullName ??
  u?.userName ??
  u?.username ??
  u?.email ??
  "Participant";

const getAvatarUrl = (u) =>
  u?.avatar ?? u?.profileImage ?? u?.profilePic ?? u?.image ?? null;

const getUserId = (u) => u?._id ?? u?.id ?? u?.userId ?? u?.memberId ?? null;

const getInitials = (name) => {
  const s = String(name || "").trim();
  if (!s) return "NA";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[1]?.[0] || "" : parts[0]?.[1] || "";
  return (a + b).toUpperCase();
};

const extractLiveKitData = (resp) => {
  if (!resp) return null;
  const d = resp?.data ?? resp;
  const token = d?.livekitToken ?? d?.tableToken ?? null;
  const serverUrl = d?.livekitServerUrl ?? d?.liveKitServerUrl ?? null;
  const sessionId = d?.sessionId ?? null;
  const tableId = d?.tableId ?? null;
  const roomName = d?.livekitRoomName ?? d?.roomName ?? null;
  if (!token || !serverUrl) return null;
  return { token, serverUrl, sessionId, tableId, roomName, raw: d };
};

function seatPositions(count, radius = 100) {
  const items = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    items.push({
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      angle,
    });
  }
  return items;
}

// ---------------------------------------------------------------------------
// DEVICE ERROR HELPER
// ---------------------------------------------------------------------------

const showDeviceError = (e, type) => {
  const map = {
    NotAllowedError: type === "mic" ? "MIC_DENIED" : "CAMERA_DENIED",
    PermissionDeniedError: type === "mic" ? "MIC_DENIED" : "CAMERA_DENIED",
    NotReadableError: type === "mic" ? "MIC_IN_USE" : "CAMERA_IN_USE",
    NotFoundError: type === "mic" ? "MIC_NOT_FOUND" : "CAMERA_NOT_FOUND",
  };
  const key = map[e.name] || "GENERIC_ERROR";
  toast.error(DEVICE_ERRORS[key].message, { duration: 5000 });
};

// ---------------------------------------------------------------------------
// 3-D ROUND TABLE
// ---------------------------------------------------------------------------

const RoundTable3D = memo(({ seats, tableIcon, tableName }) => {
  const positions = useMemo(() => seatPositions(8, 100), []);

  return (
    <div
      className="relative mx-auto w-[300px] h-[280px]"
      style={{ perspective: "900px" }}
      aria-label="Round table seating diagram"
      role="img"
    >
      {/* 3-D surface layer */}
      <div
        className="absolute inset-0"
        style={{
          transformStyle: "preserve-3d",
          transform: "rotateX(45deg)",
        }}
        aria-hidden="true"
      >
        {/* Shadow disc */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(-12px)",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.4)",
            filter: "blur(22px)",
          }}
        />

        {/* Table surface */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 35% 30%, #E7D9C0 0%, #C6A87C 35%, #8A6F4A 75%, #5A4530 100%)",
            boxShadow:
              "inset 0 0 0 2px rgba(60,40,20,0.45), inset 0 -6px 18px rgba(0,0,0,0.35), 0 6px 14px rgba(0,0,0,0.5)",
            border: "1px solid rgba(0,0,0,0.4)",
          }}
        />

        {/* Inner ring */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(0.5px)",
            width: 160,
            height: 160,
            borderRadius: "50%",
            border: "1px solid rgba(60,40,20,0.35)",
            opacity: 0.7,
          }}
        />

        {/* Centre plate */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(1px)",
            width: 96,
            height: 96,
            borderRadius: "50%",
            background: tableIcon
              ? "radial-gradient(circle at 35% 30%, #fdf6e8 0%, #f3e6cd 50%, #d9c599 100%)"
              : "rgba(20,12,4,0.35)",
            border: tableIcon
              ? "1px solid rgba(60,40,20,0.45)"
              : "1.5px dashed rgba(255,255,255,0.18)",
            boxShadow: tableIcon
              ? "inset 0 -2px 6px rgba(0,0,0,0.25)"
              : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {tableIcon ? (
            <img
              src={tableIcon}
              alt={tableName}
              style={{
                maxWidth: "72%",
                maxHeight: "72%",
                objectFit: "contain",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = film;
              }}
            />
          ) : (
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 13,
                color: "rgba(60,40,20,0.85)",
                padding: "0 8px",
                lineHeight: 1.1,
                textAlign: "center",
              }}
            >
              {tableName}
            </span>
          )}
        </div>

        {/* Chairs */}
        {positions.map((p, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: "50%",
              top: "50%",
              transform: `translate(-50%, -50%) translate(${p.x}px, ${p.y}px) translateZ(6px) rotate(${(p.angle * 180) / Math.PI + 90}deg)`,
            }}
          >
            <div
              style={{
                width: 26,
                height: 20,
                borderRadius: "6px 6px 4px 4px",
                background:
                  "linear-gradient(180deg, #2a3a36 0%, #1a2522 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow:
                  "0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: -2,
                transform: "translateX(-50%) rotateX(-50deg)",
                transformOrigin: "bottom center",
                width: 22,
                height: 13,
                borderRadius: "4px 4px 2px 2px",
                background:
                  "linear-gradient(180deg, #3a4a45 0%, #1f2c28 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            />
          </div>
        ))}
      </div>

      {/* Avatar layer */}
      <div className="absolute inset-0" aria-hidden="true">
        {positions.map((p, i) => {
          const occupant = seats[i] ?? null;
          const label = SEAT_LABELS_8[i] ?? String(i + 1);
          const name = occupant ? getName(occupant) : "";
          const avatarUrl = occupant ? getAvatarUrl(occupant) : null;
          const initials = name ? getInitials(name) : label;
          const isEmpty = !occupant;

          return (
            <div
              key={i}
              className="absolute top-1/2 left-1/2"
              style={{
                transform: `translate(${p.x - 16}px, ${p.y * 0.72 - 42}px)`,
              }}
            >
              {isEmpty ? (
                <div
                  className="w-8 h-8 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center"
                  title={`Seat ${label} — open`}
                >
                  <span className="text-[9px] text-white/30 font-semibold">
                    {label}
                  </span>
                </div>
              ) : (
                <div
                  className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/30 shadow-lg"
                  style={{ boxShadow: "0 3px 10px -2px rgba(0,0,0,0.55)" }}
                  title={name}
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold bg-[#0A1D1A] text-[#C6A87C]">
                      {initials}
                    </span>
                  )}
                </div>
              )}
              <div className="flex justify-center mt-0.5">
                <span
                  className={`text-[8px] font-bold px-1 rounded ${
                    isEmpty ? "text-white/20" : "text-[#C6A87C]"
                  }`}
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
RoundTable3D.displayName = "RoundTable3D";

// ---------------------------------------------------------------------------
// SEAT ROWS
// ---------------------------------------------------------------------------

const ParticipantRow = memo(({ occupant, index }) => {
  const name = getName(occupant);
  const avatarUrl = getAvatarUrl(occupant);
  const initials = getInitials(name);
  const label = SEAT_LABELS_8[index] ?? String(index + 1);

  return (
    <div className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-colors">
      <span className="w-6 h-6 rounded-full bg-[#C6A87C]/15 border border-[#C6A87C]/30 text-[#C6A87C] text-[10px] font-bold flex items-center justify-center shrink-0">
        {label}
      </span>
      <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20 shrink-0">
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold bg-[#0A1D1A] text-[#C6A87C]">
            {initials}
          </span>
        )}
      </div>
      <span className="text-sm text-white/80 truncate flex-1">{name}</span>
    </div>
  );
});
ParticipantRow.displayName = "ParticipantRow";

const EmptySeatRow = memo(({ index }) => {
  const label = SEAT_LABELS_8[index] ?? String(index + 1);
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl opacity-40">
      <span className="w-6 h-6 rounded-full border border-dashed border-white/20 text-white/30 text-[10px] font-bold flex items-center justify-center shrink-0">
        {label}
      </span>
      <div className="w-8 h-8 rounded-full border-2 border-dashed border-white/15" />
      <span className="text-sm text-white/30 italic">Open seat</span>
    </div>
  );
});
EmptySeatRow.displayName = "EmptySeatRow";

// ---------------------------------------------------------------------------
// DEVICE CONTROL BUTTON
// ---------------------------------------------------------------------------

const DeviceButton = memo(({ active, onToggle, activeIcon: ActiveIcon, inactiveIcon: InactiveIcon, activeLabel, inactiveLabel }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`flex flex-col items-center gap-2 px-6 py-4 rounded-2xl border transition-all ${
      active
        ? "border-[#C6A87C]/40 bg-[#C6A87C]/10 text-[#C6A87C]"
        : "border-white/[0.08] bg-white/[0.03] text-white/40"
    } hover:border-white/30 hover:text-white`}
  >
    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.05]">
      {active ? <ActiveIcon size={18} aria-hidden="true" /> : <InactiveIcon size={18} aria-hidden="true" />}
    </div>
    <span className="text-xs font-medium">{active ? activeLabel : inactiveLabel}</span>
  </button>
));
DeviceButton.displayName = "DeviceButton";

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

function JoinMeetingLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  //   const dispatch = useDispatch();

  const {
    eventId,
    sessionId: paramSessionId,
    tableId: paramTableId,
  } = useParams();

  const sessionId = paramSessionId || location.state?.sessionId || null;
  const tableId = paramTableId || location.state?.tableId || null;

  const tableNameFromState = location.state?.tableName;
  const tableIconFromState =
    location.state?.tableIcon || location.state?.sponcer?.image || null;
  const capacityFromState = location.state?.capacity;
  const joinResponseFromState = location.state?.joinResponse ?? null;

  // ---------------------------------------------------------------------------
  // Participants state (direct API, no Redux)
  // ---------------------------------------------------------------------------
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [participantsError, setParticipantsError] = useState(null);
  const hasLoadedOnceRef = useRef(false);
  const [joinRespLoading, setJoinRespLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // LiveKit / join response (sessionStorage cache)
  // ---------------------------------------------------------------------------
  const cachedJoinResponse = useMemo(() => {
    if (!sessionId || !tableId) return null;
    try {
      const raw = sessionStorage.getItem(joinCacheKey(sessionId, tableId));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [sessionId, tableId]);

  console.log("JOIN CACHE", {
    sessionId,
    tableId,
    key: joinCacheKey(sessionId, tableId),
    value: sessionStorage.getItem(joinCacheKey(sessionId, tableId)),
  });

  const effectiveJoinResponse = joinResponseFromState || cachedJoinResponse;
  const isQueuedJoin =
    (effectiveJoinResponse?.queued ?? effectiveJoinResponse?.data?.queued) ===
    true;

  const liveKitData = useMemo(
    () => extractLiveKitData(effectiveJoinResponse),
    [effectiveJoinResponse],
  );
  useEffect(() => {
    if (!sessionId || !tableId) return;
    if (!joinResponseFromState) return;

    sessionStorage.setItem(
      joinCacheKey(sessionId, tableId),
      JSON.stringify(joinResponseFromState),
    );
  }, [joinResponseFromState, sessionId, tableId]);

  // ---------------------------------------------------------------------------
  // Page meta
  // ---------------------------------------------------------------------------
  useEffect(() => {
    document.title = "Join Meeting | theglobalapp";
  }, []);

  // ---------------------------------------------------------------------------
  // Validate token
  // ---------------------------------------------------------------------------
  const canConnectToThisTable = useMemo(() => {
    if (!liveKitData) return false;
    const { token, serverUrl, sessionId: lkSid, tableId: lkTid } = liveKitData;
    if (!token || !serverUrl) return false;
    if (sessionId && lkSid && String(lkSid) !== String(sessionId)) return false;
    if (tableId && lkTid && String(lkTid) !== String(tableId)) return false;
    return true;
  }, [liveKitData, sessionId, tableId]);

  // ---------------------------------------------------------------------------
  // Derived table data
  // ---------------------------------------------------------------------------
  const tableName =
    tableNameFromState ||
    participants?.[0]?.tableName ||
    "Permitted Media Areas";

  const tableIcon =
    tableIconFromState ||
    participants?.[0]?.sponsorDetails?.image ||
    participants?.[0]?.tableIcon ||
    film;

  const capacity = useMemo(() => {
    const c = capacityFromState ?? participants?.[0]?.capacity ?? 8;
    return Math.max(1, Number(c) || 8);
  }, [capacityFromState, participants]);

  const filled = Array.isArray(participants) ? participants.length : 0;
  const seatsLeft = Math.max(0, capacity - filled);

  // ---------------------------------------------------------------------------
  // Poll participants (direct API)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId || !tableId) return;

    let cancelled = false;
    let intervalId = null;

    const poll = async () => {
      try {
        const res = await getTableParticipants(sessionId, tableId);
        if (cancelled) return;

        const raw = res?.data ?? res;
        const result = Array.isArray(raw?.participants)
          ? raw.participants
          : Array.isArray(res?.participants)
            ? res.participants
            : [];

        setParticipants((prev) => {
          const prevIds = prev.map((p) => getUserId(p)).join(",");
          const nextIds = result.map((p) => getUserId(p)).join(",");
          if (prevIds === nextIds) return prev;
          return result;
        });
        setParticipantsError(null);
      } catch (e) {
        if (cancelled) return;
        if (!hasLoadedOnceRef.current) {
          setParticipantsError(e?.message || "Failed to load participants.");
        }
      } finally {
        if (!cancelled) {
          setParticipantsLoading(false);
          hasLoadedOnceRef.current = true;
        }
      }
    };

    poll();
    intervalId = setInterval(poll, 3000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [sessionId, tableId]);

  // ---------------------------------------------------------------------------
  // Leave logic
  // ---------------------------------------------------------------------------
  const didLeaveRef = useRef(false);

  const leaveOnce = useCallback(async () => {
    if (didLeaveRef.current) return;
    if (!sessionId || !tableId) return;

    const hasReservation =
      !!effectiveJoinResponse ||
      !!sessionStorage.getItem(joinCacheKey(sessionId, tableId));

    if (!hasReservation) return;

    didLeaveRef.current = true;

    try {
      await leaveSessionTable(sessionId);
    } catch {
      // ignore
    } finally {
      sessionStorage.removeItem(joinCacheKey(sessionId, tableId));
    }
  }, [sessionId, tableId, effectiveJoinResponse]);

  // ✅ Ask confirm on tab close/refresh, and only if user REALLY leaves -> call keepalive leave API
  useEffect(() => {
    if (!sessionId || !tableId) return;

    // Only prompt if we actually have a "reservation"/join response cached
    let hasReservation = false;
    try {
      hasReservation =
        !!effectiveJoinResponse ||
        !!sessionStorage.getItem(joinCacheKey(sessionId, tableId));
    } catch {
      hasReservation = !!effectiveJoinResponse;
    }

    if (!hasReservation) return;

    // 1) Trigger browser confirm dialog
    const onBeforeUnload = (e) => {
      if (didLeaveRef.current) return;
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    };

    // 2) Fires only if user confirms leaving (page actually unloads)
    const onPageHide = () => {
      if (didLeaveRef.current) return;
      didLeaveRef.current = true;

      // Best-effort leave call during unload
      leaveSessionTableKeepalive(sessionId);

      // cleanup cache
      try {
        sessionStorage.removeItem(joinCacheKey(sessionId, tableId));
      } catch {}
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [sessionId, tableId, effectiveJoinResponse]);

  // ---------------------------------------------------------------------------
  // Redirect if token missing
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId || !tableId) return;
    const timer = setTimeout(() => {
      if (!joinRespLoading && !canConnectToThisTable && !isQueuedJoin) {
        navigate(`/events/${eventId}/sessions/${sessionId}/tables/${tableId}`, {
          replace: true,
          state: {
            tokenMissing: true,
            tableName: tableNameFromState,
            tableIcon: tableIconFromState,
            capacity: capacityFromState,
            session: location.state?.session,
          },
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [
    joinRespLoading,
    canConnectToThisTable,
    sessionId,
    tableId,
    eventId,
    navigate,
    tableNameFromState,
    tableIconFromState,
    capacityFromState,
    location.state,
    isQueuedJoin,
  ]);

  useEffect(() => {
    if (!sessionId || !tableId) return;
    if (effectiveJoinResponse) return;

    let cancelled = false;
    (async () => {
      setJoinRespLoading(true);
      try {
        const resp = await joinSessionTable(sessionId, tableId);
        if (cancelled) return;

        const payload = resp?.data ?? resp;
        sessionStorage.setItem(
          joinCacheKey(sessionId, tableId),
          JSON.stringify(payload),
        );
        // easiest: reload current route state by navigating to same page with state
        navigate(".", {
          replace: true,
          state: { ...location.state, joinResponse: payload },
        });
      } catch {
        // let redirect logic handle it
      } finally {
        if (!cancelled) setJoinRespLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, tableId, effectiveJoinResponse, navigate, location.state]);

  // ---------------------------------------------------------------------------
  // Camera / Mic
  // ---------------------------------------------------------------------------
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const audioTrackRef = useRef(null);

  const [camOn, setCamOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinUiError, setJoinUiError] = useState("");

  // Init devices once
  useEffect(() => {
    let cancelled = false;

    const initDevices = async () => {
      let camTrack = null;
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        camTrack = s.getVideoTracks()[0] || null;
      } catch (e) {
        console.warn("Camera init failed:", e.name);
      }

      let micTrack = null;
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: true,
        });
        micTrack = s.getAudioTracks()[0] || null;
      } catch (e) {
        console.warn("Mic init failed:", e.name);
      }

      if (cancelled) {
        camTrack?.stop();
        micTrack?.stop();
        return;
      }

      cameraTrackRef.current = camTrack;
      audioTrackRef.current = micTrack;

      const tracks = [camTrack, micTrack].filter(Boolean);
      if (tracks.length > 0) {
        const stream = new MediaStream(tracks);
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      }

      setCamOn(!!camTrack);
      setMicOn(!!micTrack);
    };

    initDevices();

    return () => {
      cancelled = true;
      cameraTrackRef.current?.stop();
      audioTrackRef.current?.stop();
      cameraTrackRef.current = null;
      audioTrackRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, []);

  const stopStream = useCallback(() => {
    cameraTrackRef.current?.stop();
    audioTrackRef.current?.stop();
    cameraTrackRef.current = null;
    audioTrackRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const toggleMic = useCallback(async () => {
    if (micOn) {
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        streamRef.current?.removeTrack(audioTrackRef.current);
        audioTrackRef.current = null;
      }
      setMicOn(false);
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });
      const micTrack = s.getAudioTracks()[0];
      if (!micTrack) return;
      audioTrackRef.current = micTrack;
      if (streamRef.current) {
        streamRef.current.addTrack(micTrack);
      } else {
        streamRef.current = new MediaStream([micTrack]);
      }
      setMicOn(true);
    } catch (e) {
      console.error("Mic ON failed:", e.name);
      audioTrackRef.current = null;
      setMicOn(false);
      showDeviceError(e, "mic");
    }
  }, [micOn]);

  const toggleCam = useCallback(async () => {
    if (camOn) {
      if (cameraTrackRef.current) {
        cameraTrackRef.current.stop();
        streamRef.current?.removeTrack(cameraTrackRef.current);
        cameraTrackRef.current = null;
      }
      setCamOn(false);
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      const camTrack = s.getVideoTracks()[0];
      if (!camTrack) return;
      cameraTrackRef.current = camTrack;
      if (streamRef.current) {
        streamRef.current.addTrack(camTrack);
      } else {
        streamRef.current = new MediaStream([camTrack]);
      }
      if (videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
        await videoRef.current.play().catch(() => {});
      }
      setCamOn(true);
    } catch (e) {
      console.error("Camera ON failed:", e.name);
      cameraTrackRef.current = null;
      setCamOn(false);
      showDeviceError(e, "cam");
    }
  }, [camOn]);

  // ---------------------------------------------------------------------------
  // 8-seat grid
  // ---------------------------------------------------------------------------
  const seats8 = useMemo(() => {
    const p = Array.isArray(participants) ? participants : [];
    const list = Array.from({ length: 8 }, () => null);
    for (let i = 0; i < Math.min(p.length, 8); i++) list[i] = p[i];
    return list;
  }, [participants]);

  // ---------------------------------------------------------------------------
  // Join handler
  // ---------------------------------------------------------------------------
  const handleJoinNow = useCallback(() => {
    if (!sessionId || !tableId) return;
    setJoinUiError("");

    if (!canConnectToThisTable || !liveKitData) {
      setJoinUiError("LiveKit token not found. Redirecting back...");
      setTimeout(() => navigate(-1), 800);
      return;
    }

    setJoining(true);
    stopStream();

    const responseForMeetingRoom = {
      success: true,
      data: {
        ...liveKitData.raw,
        livekitToken: liveKitData.token,
        tableToken: liveKitData.token,
        livekitServerUrl: liveKitData.serverUrl,
        livekitRoomName: liveKitData.roomName,
        sessionId,
        tableId,
      },
    };

    navigate("/meeting-room", {
      state: {
        eventId,
        sessionId,
        tableId,
        tableName,
        tableIcon,
        initialMicEnabled: micOn,
        initialCameraEnabled: camOn,
        response: responseForMeetingRoom,
      },
      replace: false,
    });
  }, [
    canConnectToThisTable,
    liveKitData,
    eventId,
    micOn,
    camOn,
    navigate,
    sessionId,
    tableId,
    tableName,
    tableIcon,
    stopStream,
  ]);

  const handleBack = useCallback(() => {
    stopStream();
    navigate(-1);
  }, [stopStream, navigate]);

  const infoText = useMemo(() => {
    if (micOn && camOn) return "You will join with mic and camera on";
    if (micOn && !camOn) return "You will join with mic on, camera off";
    if (!micOn && camOn) return "You will join with mic off, camera on";
    return "You will join with mic and camera off";
  }, [micOn, camOn]);

  // ---------------------------------------------------------------------------
  // Missing params guard
  // ---------------------------------------------------------------------------
  if (!sessionId || !tableId) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="glass rounded-2xl p-10 max-w-md w-full mx-4 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="font-display text-2xl text-white">
            Missing session/table
          </h2>
          <p className="text-white/55 text-sm">
            sessionId or tableId not found. Please go back and try again.
          </p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/[0.12] text-white/70 hover:border-white/30 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (isQueuedJoin) {
    const q = effectiveJoinResponse?.data || effectiveJoinResponse;
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="glass rounded-2xl p-10 max-w-md w-full mx-4 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-[#C6A87C]/15 flex items-center justify-center mx-auto">
            <Users size={24} className="text-[#C6A87C]" />
          </div>
          <h2 className="font-display text-2xl text-white">
            {q?.modal?.title || "Table is full"}
          </h2>
          <p className="text-white/55 text-sm">
            {q?.modal?.message || q?.waitMessage || "Please wait..."}
          </p>
          {q?.queuePosition != null && (
            <p className="text-[#C6A87C] font-semibold">
              Queue position: {q.queuePosition}
            </p>
          )}
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/[0.12] text-white/70 hover:border-white/30 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // MAIN RENDER
  // ---------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      {/* ── HERO ── */}
      <section className="border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {/* Back */}
          <button
            type="button"
            onClick={handleBack}
            className="mb-8 inline-flex items-center gap-2 text-xs text-white/55 hover:text-white/90 transition-colors"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back
          </button>

          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              {/* Live badge */}
              <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2A7E5D]/15 border border-[#2A7E5D]/40">
                <span
                  className="w-1.5 h-1.5 rounded-full bg-[#7BC8A6] animate-pulse"
                  aria-hidden="true"
                />
                <span className="text-[10px] font-semibold tracking-widest uppercase text-[#7BC8A6]">
                  Ready to join
                </span>
              </div>

              <h1
                className="font-light text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-4 text-white"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {tableName}
              </h1>

              <p className="text-white/55 text-sm flex items-center gap-2">
                <Users
                  size={13}
                  className="text-[#C6A87C]"
                  aria-hidden="true"
                />
                {filled} of {capacity} seats filled ·{" "}
                <span className="text-[#7BC8A6]">{seatsLeft} open</span>
              </p>
            </div>

            <div className="md:col-span-4 flex md:justify-end">
              <p className="text-xs text-white/40 italic">{infoText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* ── LEFT: Camera preview + controls ── */}
            <div className="space-y-6">
              {/* Camera preview card */}
              <div
                className="rounded-2xl overflow-hidden border border-white/[0.06]"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                {/* Video / off state */}
                <div className="relative bg-black/40 aspect-video">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      transform: "scaleX(-1)",
                      display: camOn ? "block" : "none",
                    }}
                  />

                  {!camOn && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                      <div className="w-16 h-16 rounded-2xl bg-white/[0.05] border border-white/[0.08] flex items-center justify-center">
                        <VideoOff
                          size={28}
                          className="text-white/30"
                          aria-hidden="true"
                        />
                      </div>
                      <span className="text-white/30 text-sm">
                        Camera is off
                      </span>
                    </div>
                  )}

                  {/* Camera status chip */}
                  <div className="absolute top-3 left-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${
                        camOn
                          ? "bg-[#2A7E5D]/20 border-[#2A7E5D]/40 text-[#7BC8A6]"
                          : "bg-white/[0.04] border-white/[0.08] text-white/30"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${camOn ? "bg-[#7BC8A6] animate-pulse" : "bg-white/20"}`}
                      />
                      {camOn ? "Live preview" : "Camera off"}
                    </span>
                  </div>
                </div>

                {/* Controls row */}
                <div className="p-5">
                  <p className="text-xs text-white/40 mb-4 font-medium uppercase tracking-widest">
                    Device controls
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <DeviceButton
                      active={micOn}
                      onToggle={toggleMic}
                      activeIcon={Mic}
                      inactiveIcon={MicOff}
                      activeLabel="Mute"
                      inactiveLabel="Unmute"
                    />
                    <DeviceButton
                      active={camOn}
                      onToggle={toggleCam}
                      activeIcon={Video}
                      inactiveIcon={VideoOff}
                      activeLabel="Stop Video"
                      inactiveLabel="Start Video"
                    />
                  </div>
                </div>
              </div>

              {/* Status info card */}
              <div
                className="rounded-2xl p-5 border border-white/[0.06]"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#C6A87C]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[#C6A87C] text-sm">ℹ</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-white/70">{infoText}</p>
                    <p className="text-xs text-white/35">
                      You can change these settings after joining
                    </p>
                  </div>
                </div>
              </div>

              {/* Error */}
              {joinUiError && (
                <div className="rounded-2xl p-4 border border-red-500/20 bg-red-500/[0.06] flex items-center gap-3">
                  <span className="text-red-400 text-sm">⚠</span>
                  <span className="text-red-400 text-sm">{joinUiError}</span>
                </div>
              )}

              {/* Join button */}
              <button
                type="button"
                onClick={handleJoinNow}
                disabled={joining || !sessionId || !tableId}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-[#C6A87C] text-[#061412] font-semibold hover:bg-[#D9BC93] transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {joining ? (
                  <>
                    <RefreshCw
                      size={18}
                      className="animate-spin"
                      aria-hidden="true"
                    />
                    <span>Joining…</span>
                  </>
                ) : (
                  <>
                    <LogIn
                      size={18}
                      className="group-hover:translate-x-0.5 transition-transform"
                      aria-hidden="true"
                    />
                    <span>Join Now</span>
                    {seatsLeft > 0 && (
                      <span className="ml-auto text-[#061412]/60 text-xs font-normal">
                        {seatsLeft} seat{seatsLeft !== 1 ? "s" : ""} left
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>

            {/* ── RIGHT: Table visual + participant list ── */}
            <div className="space-y-6">
              {/* 3-D Table visual */}
              <div
                className="rounded-2xl p-8 border border-white/[0.06] flex flex-col items-center"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-center justify-between w-full mb-6">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40">
                    Seating view
                  </p>
                  {participantsLoading && !hasLoadedOnceRef.current && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-white/40">
                      <RefreshCw
                        size={11}
                        className="animate-spin"
                        aria-hidden="true"
                      />
                      Loading…
                    </span>
                  )}
                </div>

                <RoundTable3D
                  seats={seats8}
                  tableIcon={tableIcon}
                  tableName={tableName}
                />

                {/* Legend */}
                <div className="mt-6 flex items-center gap-4 text-xs text-white/50 self-start">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full border-2 border-white/30 bg-[#C6A87C]/20 inline-block" />
                    Occupied
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full border-2 border-dashed border-white/20 inline-block" />
                    Open
                  </span>
                </div>
              </div>

              {/* Participant list */}
              <div
                className="rounded-2xl p-6 border border-white/[0.06]"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40 flex items-center gap-2">
                    <Users size={12} aria-hidden="true" />
                    Participants
                  </p>
                  <span className="text-xs text-white/40">
                    {filled} / {capacity}
                  </span>
                </div>

                {/* Occupancy bar */}
                <div className="mb-4">
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.min(100, Math.round((filled / capacity) * 100))}%`,
                        background:
                          filled / capacity >= 0.9
                            ? "linear-gradient(90deg, #C6A87C, #D9BC93)"
                            : filled / capacity >= 0.6
                              ? "linear-gradient(90deg, #7BC8A6, #C6A87C)"
                              : "linear-gradient(90deg, #7BC8A6, #2A7E5D)",
                      }}
                    />
                  </div>
                </div>

                {participantsError && (
                  <p className="text-xs text-red-400/80 mb-3">
                    {String(participantsError)}
                  </p>
                )}

                <div className="space-y-1">
                  {seats8.map((occupant, i) =>
                    occupant ? (
                      <ParticipantRow
                        key={getUserId(occupant) ?? i}
                        occupant={occupant}
                        index={i}
                      />
                    ) : (
                      <EmptySeatRow key={`empty-${i}`} index={i} />
                    ),
                  )}
                </div>

                {participantsLoading && !hasLoadedOnceRef.current && (
                  <div className="mt-4 space-y-2" aria-hidden="true">
                    {Array.from({ length: 3 }, (_, i) => (
                      <div
                        key={i}
                        className="h-10 rounded-xl bg-white/[0.03] animate-pulse"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default JoinMeetingLayout;